import { useBookingLocale } from '../../hooks/useBookingLocale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BookingStatus,
  DamageType,
  PhysicalDamageSubcategory,
} from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { StarRating } from '../../components/ratings/StarRating';
import { Alert, Field, Input, Muted, Stack, Strong, Textarea } from '../../components/ui';
import {
  useCanOpenStayRequestQuery,
  useOpenStayRequestMutation,
  useRevealStayInstructionsMutation,
  useStayRequestPhotoUrlMutation,
  useStayReviewStateQuery,
  useSubmitStayGuestRatingMutation,
  useSubmitStayHostRatingMutation,
  type IRevealStayInstructionsMutation,
  type IStayBookingFieldsFragment,
} from '../../services/stayService/__generated__/queries.generated';
import { getSiteProductKind } from '../../constants/siteConfig';
import { claimDetailPath } from '../../utils/productRoutes';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { Detail, Section, SectionHeading } from './StayBookingComponents';
import {
  GUEST_REVIEW_CATEGORIES,
  HOST_REVIEW_CATEGORIES,
  STAY_REQUEST_CATEGORIES,
  checkInMethodLabel,
  clockLabel,
  instructionsState,
  parseRequestAmount,
  requestDeadlineLabel,
  reviewComplete,
  stayRequestDescription,
  stayReviewView,
  type StayRequestCategory,
} from './stayLifecycle';

type Viewer = 'guest' | 'host';
type Instructions = IRevealStayInstructionsMutation['revealStayInstructions'];

const HOUR_MS = 60 * 60 * 1000;

/**
 * Door code, Wi-Fi and how to get in (spec 016 W18, B37). The guest can open them from
 * 48 hours before check-in; the host can always check what guests will see.
 */
export function CheckInDetailsSection({
  booking,
  viewer,
}: {
  booking: IStayBookingFieldsFragment;
  viewer: Viewer;
}) {
  const zone = booking.car?.location?.timezone;
  const [reveal, { loading }] = useRevealStayInstructionsMutation();
  const [details, setDetails] = useState<Instructions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const state =
    viewer === 'host'
      ? { open: true, text: 'What your guest sees 48 hours before check-in.' }
      : instructionsState(booking.stayInstructionsAvailableAt, zone);

  const open = async () => {
    setError(null);
    try {
      const { data } = await reveal({ variables: { bookingId: booking.id } });
      setDetails(data?.revealStayInstructions ?? null);
    } catch (e) {
      setError(stayErrorMessage(e, 'The check-in details could not be loaded.'));
    }
  };

  if (booking.status !== BookingStatus.Booked) return null;

  return (
    <Section data-testid="stay-check-in-details">
      <SectionHeading>Check-in details</SectionHeading>
      {details ? (
        <Stack $gap={4}>
          <Detail label="Address">{details.address}</Detail>
          <Detail label="Check-in">
            {clockLabel(details.checkInFrom)}
            {details.checkInUntil ? ` to ${clockLabel(details.checkInUntil)}` : ' or later'}
          </Detail>
          <Detail label="Checkout">By {clockLabel(details.checkOutBy)}</Detail>
          <Detail label="How to get in">
            {[
              details.selfCheckIn ? 'Self check-in' : null,
              checkInMethodLabel(details.checkInMethod),
            ]
              .filter(Boolean)
              .join(' · ') || 'Arranged with the host'}
          </Detail>
          {details.checkInInstructions ? (
            <Detail label="Instructions">
              <span style={{ whiteSpace: 'pre-line' }} data-testid="stay-check-in-instructions">
                {details.checkInInstructions}
              </span>
            </Detail>
          ) : null}
          {details.wifiName ? <Detail label="Wi-Fi">{details.wifiName}</Detail> : null}
          {details.wifiPassword ? (
            <Detail label="Wi-Fi password">{details.wifiPassword}</Detail>
          ) : null}
        </Stack>
      ) : (
        <Stack $gap={4}>
          <Muted>{state.text}</Muted>
          {state.open ? (
            <div>
              <Button
                theme="outlined"
                text="Show check-in details"
                isLoading={loading}
                onClick={open}
                dataTestId="stay-reveal-instructions"
              />
            </div>
          ) : null}
          {error ? (
            <Alert tone="error" title="Not available">
              {error}
            </Alert>
          ) : null}
        </Stack>
      )}
    </Section>
  );
}

/** Reviews after checkout (spec 016 W22): write yours, then wait for the other side. */
export function StayReviewSection({
  booking,
  viewer,
}: {
  booking: IStayBookingFieldsFragment;
  viewer: Viewer;
}) {
  const [open, setOpen] = useState(false);
  const reviewable =
    booking.status === BookingStatus.Booked || booking.status === BookingStatus.Completed;
  const { data, refetch } = useStayReviewStateQuery({
    variables: { bookingId: booking.id },
    skip: !reviewable || Date.now() < booking.endAt,
    fetchPolicy: 'cache-and-network',
  });
  if (!reviewable || Date.now() < booking.endAt || !data) return null;

  const eligibility = data.canRateBooking;
  const rating = data.getTripRating;
  const otherName =
    viewer === 'guest'
      ? booking.host?.firstName || booking.host?.displayName || 'your host'
      : booking.guest?.firstName || booking.guest?.displayName || 'your guest';
  const view = stayReviewView({
    canRate: viewer === 'guest' ? eligibility.canGuestRate : eligibility.canHostRate,
    deadline: viewer === 'guest' ? eligibility.guestRatingDeadline : eligibility.hostRatingDeadline,
    reason: eligibility.reason,
    mine: viewer === 'guest' ? rating?.guestRatedAt : rating?.hostRatedAt,
    theirs: viewer === 'guest' ? rating?.hostRatedAt : rating?.guestRatedAt,
    otherName,
    zone: booking.car?.location?.timezone,
  });
  // What the other side wrote, once the server shows it.
  const theirs = viewer === 'guest' ? rating?.stayHostRating : rating?.stayGuestRating;

  return (
    <Section data-testid="stay-review-section">
      <SectionHeading>
        {viewer === 'guest' ? 'Review your stay' : `Review ${otherName}`}
      </SectionHeading>
      <Stack $gap={4}>
        <Muted data-testid="stay-review-state">{view.text}</Muted>
        {view.kind === 'write' ? (
          <div>
            <Button
              text="Write a review"
              onClick={() => setOpen(true)}
              dataTestId="stay-review-write"
            />
          </div>
        ) : null}
        {theirs ? (
          <Stack $gap={4} data-testid="stay-review-theirs">
            <Strong>
              {otherName} rated {viewer === 'guest' ? 'you' : 'the stay'} {theirs.overall} of 5
            </Strong>
            {theirs.publicReview ? <Muted>“{theirs.publicReview}”</Muted> : null}
          </Stack>
        ) : null}
      </Stack>
      <StayReviewModal
        booking={booking}
        viewer={viewer}
        otherName={otherName}
        isOpen={open}
        onClose={() => setOpen(false)}
        onDone={() => {
          setOpen(false);
          void refetch();
        }}
      />
    </Section>
  );
}

function StayReviewModal({
  booking,
  viewer,
  otherName,
  isOpen,
  onClose,
  onDone,
}: {
  booking: IStayBookingFieldsFragment;
  viewer: Viewer;
  otherName: string;
  isOpen: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const categories = viewer === 'guest' ? GUEST_REVIEW_CATEGORIES : HOST_REVIEW_CATEGORIES;
  const keys = useMemo(() => ['overall', ...categories.map(c => c.key)], [categories]);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [publicReview, setPublicReview] = useState('');
  const [privateNote, setPrivateNote] = useState('');
  const [wouldHostAgain, setWouldHostAgain] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitGuest, { loading: savingGuest }] = useSubmitStayGuestRatingMutation();
  const [submitHost, { loading: savingHost }] = useSubmitStayHostRatingMutation();

  useEffect(() => {
    if (!isOpen) return;
    setScores({});
    setPublicReview('');
    setPrivateNote('');
    setWouldHostAgain(null);
    setError(null);
  }, [isOpen]);

  const complete = reviewComplete(scores, keys) && (viewer === 'guest' || wouldHostAgain !== null);

  const submit = async () => {
    setError(null);
    const text = {
      publicReview: publicReview.trim() || null,
      privateNote: privateNote.trim() || null,
    };
    try {
      if (viewer === 'guest') {
        await submitGuest({
          variables: {
            input: {
              bookingId: booking.id,
              rating: {
                overall: scores.overall,
                cleanliness: scores.cleanliness,
                accuracy: scores.accuracy,
                checkIn: scores.checkIn,
                communication: scores.communication,
                location: scores.location,
                value: scores.value,
                ...text,
              },
            },
          },
        });
      } else {
        await submitHost({
          variables: {
            input: {
              bookingId: booking.id,
              rating: {
                overall: scores.overall,
                cleanliness: scores.cleanliness,
                communication: scores.communication,
                houseRules: scores.houseRules,
                wouldHostAgain: Boolean(wouldHostAgain),
                ...text,
              },
            },
          },
        });
      }
      toast.success('Thanks for your review');
      onDone();
    } catch (e) {
      setError(stayErrorMessage(e, 'Your review could not be saved.'));
    }
  };

  const setScore = (key: string) => (value: number) =>
    setScores(prev => ({ ...prev, [key]: value }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Write a review" modalWidth="520px">
      <Stack $gap={6} data-testid="stay-review-modal">
        <Stack $gap={4}>
          <Strong style={{ fontSize: 20 }}>
            {viewer === 'guest'
              ? `How was ${booking.car?.name || 'your stay'}?`
              : `How was hosting ${otherName}?`}
          </Strong>
          <Muted $size="sm">
            {otherName} sees your review once they write theirs, or after 14 days.
          </Muted>
        </Stack>
        <ScoreRow label="Overall" value={scores.overall} onChange={setScore('overall')} />
        {categories.map(category => (
          <ScoreRow
            key={category.key}
            label={category.label}
            hint={category.hint}
            value={scores[category.key]}
            onChange={setScore(category.key)}
          />
        ))}
        {viewer === 'host' ? (
          <Stack $gap={4}>
            <Strong>Would you host {otherName} again?</Strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="sm"
                theme={wouldHostAgain === true ? 'filled' : 'secondary'}
                text="Yes"
                onClick={() => setWouldHostAgain(true)}
                dataTestId="stay-review-host-again-yes"
              />
              <Button
                size="sm"
                theme={wouldHostAgain === false ? 'filled' : 'secondary'}
                text="No"
                onClick={() => setWouldHostAgain(false)}
              />
            </div>
          </Stack>
        ) : null}
        <Field
          label="Public review"
          htmlFor="stay-review-public"
          hint={
            viewer === 'guest'
              ? 'Shown on the listing with your first name.'
              : 'Shown on the guest’s profile.'
          }
        >
          <Textarea
            id="stay-review-public"
            rows={4}
            maxLength={1000}
            value={publicReview}
            onChange={e => setPublicReview(e.target.value)}
            data-testid="stay-review-public"
          />
        </Field>
        <Field
          label="Private feedback"
          htmlFor="stay-review-private"
          hint={`Optional. Only our team reads it, never ${otherName}.`}
        >
          <Textarea
            id="stay-review-private"
            rows={2}
            maxLength={1000}
            value={privateNote}
            onChange={e => setPrivateNote(e.target.value)}
          />
        </Field>
        {error ? (
          <Alert tone="error" title="Not saved">
            {error}
          </Alert>
        ) : null}
        <Button
          text="Submit review"
          fullWidth
          isDisabled={!complete}
          isLoading={savingGuest || savingHost}
          onClick={submit}
          dataTestId="stay-review-submit"
        />
      </Stack>
    </Modal>
  );
}

function ScoreRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value?: number;
  onChange: (value: number) => void;
}) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      data-testid={`stay-review-score-${label.toLowerCase().replace(/\W+/g, '-')}`}
    >
      <div style={{ minWidth: 0 }}>
        <Strong>{label}</Strong>
        {hint ? (
          <Muted $size="sm" style={{ display: 'block' }}>
            {hint}
          </Muted>
        ) : null}
      </div>
      <StarRating rating={value ?? 0} onRatingChange={onChange} size="sm" showLabel={false} />
    </div>
  );
}

/**
 * Resolution Center (spec 016 W25): ask the other side for money after a stay, with
 * photos. It is a damage claim underneath, so the existing review, response and admin
 * flows apply.
 */
export function StayRequestSection({
  booking,
  viewer,
}: {
  booking: IStayBookingFieldsFragment;
  viewer: Viewer;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // The server opens the window on the checkout day; skip the call until then.
  const near = Date.now() >= booking.endAt - 24 * HOUR_MS;
  const relevant =
    near && (booking.status === BookingStatus.Booked || booking.status === BookingStatus.Completed);
  const { data } = useCanOpenStayRequestQuery({
    variables: { bookingId: booking.id },
    skip: !relevant,
    fetchPolicy: 'cache-and-network',
  });
  const answer = data?.canReportDamage;
  if (!relevant || !answer?.canReport) return null;
  const deadline = requestDeadlineLabel(answer.deadline, booking.car?.location?.timezone);

  return (
    <Section data-testid="stay-request-section">
      <SectionHeading>Resolution Center</SectionHeading>
      <Stack $gap={4}>
        <Muted>
          {viewer === 'host'
            ? 'Something damaged, missing, or extra cleaning needed? Ask the guest to pay for it, with photos.'
            : 'Something went wrong with this stay? Send a request with photos.'}
          {deadline ? ` ${deadline}.` : ''}
        </Muted>
        <div>
          <Button
            theme="outlined"
            text="Open a request"
            onClick={() => setOpen(true)}
            dataTestId="stay-request-open"
          />
        </div>
      </Stack>
      <StayRequestModal
        booking={booking}
        isOpen={open}
        onClose={() => setOpen(false)}
        onSent={claimId => {
          setOpen(false);
          navigate(claimDetailPath(viewer, claimId, getSiteProductKind()));
        }}
      />
    </Section>
  );
}

function StayRequestModal({
  booking,
  isOpen,
  onClose,
  onSent,
}: {
  booking: IStayBookingFieldsFragment;
  isOpen: boolean;
  onClose: () => void;
  onSent: (claimId: string) => void;
}) {
  // Spec 028: the currency the stay was booked in.
  const requestLocale = useBookingLocale(booking.id, 'any');
  const [category, setCategory] = useState<StayRequestCategory>('damage');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [photoUrl] = useStayRequestPhotoUrlMutation();
  const [openRequest] = useOpenStayRequestMutation();

  useEffect(() => {
    if (!isOpen) return;
    setCategory('damage');
    setDescription('');
    setAmount('');
    setFiles([]);
    setError(null);
  }, [isOpen]);

  const previews = useMemo(() => files.map(file => URL.createObjectURL(file)), [files]);
  useEffect(() => () => previews.forEach(url => URL.revokeObjectURL(url)), [previews]);

  const parsedAmount = amount.trim() ? parseRequestAmount(amount) : null;
  const amountInvalid = amount.trim() !== '' && parsedAmount === null;
  const ready = description.trim().length >= 10 && files.length > 0 && !amountInvalid;

  const upload = async (file: File, claimId: string): Promise<string> => {
    const { data } = await photoUrl({
      variables: { fileType: file.type || 'image/jpeg', claimId, photoType: 'after' },
    });
    const target = data?.getPresignedUrlForDamageClaim;
    if (!target?.url || !target.key) throw new Error('The photo could not be uploaded.');
    const response = await fetch(target.url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'image/jpeg' },
    });
    if (!response.ok) throw new Error(`The photo could not be uploaded (${response.status}).`);
    return toPublicS3ObjectUrl(target.key);
  };

  const send = async () => {
    setSaving(true);
    setError(null);
    try {
      const folder = `temp-${Date.now()}`;
      const photos: string[] = [];
      for (const file of files) photos.push(await upload(file, folder));
      const { data } = await openRequest({
        variables: {
          input: {
            bookingId: booking.id,
            damageType: DamageType.Physical,
            physicalDamageSubcategory: PhysicalDamageSubcategory.SomethingElse,
            damageDescription: stayRequestDescription(category, description),
            afterPhotos: photos,
            ...(parsedAmount !== null ? { claimAmount: parsedAmount } : {}),
          },
        },
      });
      const claim = data?.createDamageClaim;
      if (!claim) throw new Error('The request could not be sent.');
      toast.success(`Request #${claim.claimNum} sent`);
      onSent(claim.id);
    } catch (e) {
      setError(stayErrorMessage(e, 'The request could not be sent.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Open a request" modalWidth="520px">
      <Stack $gap={6} data-testid="stay-request-modal">
        <Strong style={{ fontSize: 20 }}>Open a request</Strong>
        <Field label="What happened?">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {STAY_REQUEST_CATEGORIES.map(option => (
              <Button
                key={option.key}
                size="sm"
                theme={category === option.key ? 'filled' : 'secondary'}
                text={option.label}
                onClick={() => setCategory(option.key)}
                dataTestId={`stay-request-category-${option.key}`}
              />
            ))}
          </div>
          <Muted $size="sm">
            {STAY_REQUEST_CATEGORIES.find(option => option.key === category)?.hint}
          </Muted>
        </Field>
        <Field
          label="Describe it"
          htmlFor="stay-request-description"
          hint="What happened, where, and what it costs to fix. At least 10 characters."
          required
        >
          <Textarea
            id="stay-request-description"
            rows={4}
            maxLength={2000}
            value={description}
            onChange={e => setDescription(e.target.value)}
            data-testid="stay-request-description"
          />
        </Field>
        <Field
          label={`Amount you are asking for (${requestLocale.currency})`}
          htmlFor="stay-request-amount"
          hint="Optional. Add receipts or quotes as photos."
          error={amountInvalid ? 'Enter an amount like 120 or 120.50.' : undefined}
        >
          <Input
            id="stay-request-amount"
            inputMode="decimal"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            aria-invalid={amountInvalid}
            data-testid="stay-request-amount"
          />
        </Field>
        <Field label="Add photos" hint="At least one photo of the problem." required>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              const picked = Array.from(e.target.files ?? []).slice(0, 10);
              setFiles(prev => [...prev, ...picked].slice(0, 10));
              e.target.value = '';
            }}
            data-testid="stay-request-photos"
          />
          <div>
            <Button
              theme="secondary"
              size="sm"
              text={files.length ? `Add more photos (${files.length} added)` : '+ Add photos'}
              isDisabled={files.length >= 10}
              onClick={() => fileInput.current?.click()}
            />
          </div>
          {previews.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {previews.map((url, index) => (
                <button
                  key={url}
                  type="button"
                  title="Remove photo"
                  onClick={() => setFiles(prev => prev.filter((_, i) => i !== index))}
                  style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer' }}
                >
                  <img
                    src={url}
                    alt=""
                    style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                  />
                </button>
              ))}
            </div>
          ) : null}
        </Field>
        {error ? (
          <Alert tone="error" title="Not sent">
            {error}
          </Alert>
        ) : null}
        <Button
          text="Send request"
          fullWidth
          isDisabled={!ready}
          isLoading={saving}
          onClick={send}
          dataTestId="stay-request-send"
        />
        <Muted $size="sm">
          The other side has 48 hours to respond. Our team reviews every request.
        </Muted>
      </Stack>
    </Modal>
  );
}
